import {graphql} from 'gatsby'
import React, {useReducer} from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import {Consents} from '@/components/consents'
import Input from '@/components/input'
import Select from '@/components/select'
import {Text} from '@/components/text'
import {useConsents} from '@/hooks/use-consents'
import useCountryList from '@/hooks/use-country-list'
import useFormErrors from '@/hooks/use-form-errors'
import {useIsCompanyEmail} from '@/hooks/use-is-company-email'
import useNavigate from '@/hooks/use-navigate'
import {useSubmitHubspotForm} from '@/hooks/use-submit-hubspot-form'
import locales from '@/locale/locales'
import {useGlobalStrings} from '@/providers/global-strings'
import {useGoogleReCaptcha} from '@/providers/google-recaptcha'
import {useLocale} from '@/providers/locale'
import {tokens} from '@/theme'
import getEnvironmentField from '@/utils/get-environment-field'
import {isGenericEmail} from '@/utils/is-generic-email'

import type {HubspotGlobalFormFragment} from '@/graphql-types'
import type {CountryCode} from '@/locale/locales'
import type {ConsentTypes} from '@/types/consents'
import type {HubspotContactProps} from '@/types/hubspot-contact-props'
import type {CountryCode as PhoneCountryCode} from 'libphonenumber-js'

import * as s from '../lead-form.styles'
import {bigformCompleted, bigformInteracted, bigformReached} from '../tracking'

type NotSupportedCountryCode = Exclude<Lowercase<PhoneCountryCode>, CountryCode>

export interface State {
    email: string
    hasConsented: boolean
    country: NotSupportedCountryCode | ''
}

enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

type Action<TKey extends keyof State> = SetValue<TKey>

const reducer = <TKey extends keyof State>(state: State, action: Action<TKey>) => {
    switch (action.type) {
        case ActionType.SET_VALUE: {
            return {
                ...state,
                [action.key]: action.value,
            }
        }

        default: {
            throw new Error('Unrecognized action')
        }
    }
}

interface ErrorsLabels {
    this_field_is_required: string
    please_enter_a_work_email: string
}

const validateInputs = (state: State, content: ErrorsLabels, isValidEmail: boolean) => {
    type Error = {
        key: keyof State
        value: string
    }

    const errors: Error[] = []

    if (!state.country) {
        errors.push({
            key: 'country',
            value: content.this_field_is_required,
        })
    }

    if (!state.email.trim() || !isValidEmail) {
        errors.push({
            key: 'email',
            value: content.please_enter_a_work_email,
        })
    }

    return errors
}

export interface Props {
    hubspotProperties?: Partial<HubspotContactProps>
    content: HubspotGlobalFormFragment['data']
}

export const HubspotGlobalForm = ({hubspotProperties, content}: Props) => {
    const globalStrings = useGlobalStrings()
    const currentLocale = useLocale()
    const navigate = useNavigate()
    const countryList = useCountryList()
    const supportedLocales = locales.map((locale) => locale.country.toUpperCase())
    const {loadReCaptcha} = useGoogleReCaptcha()

    const form = getEnvironmentField({
        production: content.form_production,
        staging: content.form_staging,
    })
    const imperativeConsents = getEnvironmentField({
        production: content.imperative_consents_production,
        staging: content.imperative_consents_staging,
    })

    const {recaptchaHubspotSubmit, isLoading} = useSubmitHubspotForm(form.formId, false)
    const initialState: State = {
        email: '',
        hasConsented: false,
        country: '',
    }

    const [state, dispatch] = useReducer(reducer, initialState)
    const {errors, setError, clearError} = useFormErrors(state)
    const isValidEmail = useIsCompanyEmail(state.email || '')

    const {consentsProps, consentsPayload, validateConsents} = useConsents({
        communicationConsents: form.consents.communicationConsents,
        type: form.consents.type as ConsentTypes,
        imperativeConsents,
    })

    const setValue = <TKey extends keyof State>(key: TKey, value: State[TKey]) => {
        dispatch({
            type: ActionType.SET_VALUE,
            key,
            value,
        })
        bigformInteracted(key)
    }

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Validate inputs
        const errors = validateInputs(state, globalStrings, isValidEmail)

        if (!validateConsents()) {
            errors.push({
                key: 'hasConsented',
                value: 'Please accept all consents',
            })
        }

        if (errors.length) {
            errors.forEach((error) => {
                setError(error.key, error.value)
            })
            return
        }

        await recaptchaHubspotSubmit(
            {
                ...hubspotProperties,
                email: state.email,
                country: state.country,
                genericEmail: isGenericEmail(state.email),
            },
            consentsPayload,
        )

        bigformCompleted(state)

        // Redirect user
        navigate('/thankyou-global', {
            state: {
                email: state.email,
                country: countryList.find((country) => country.code === state.country.toUpperCase())
                    ?.name,
            },
        })
    }

    const handleInputChange: typeof setValue = (key, value) => {
        loadReCaptcha()
        clearError(key)
        setValue(key, value)
    }

    React.useEffect(() => {
        bigformReached({
            country: currentLocale.country,
        })
    }, [currentLocale.country])

    return (
        <s.Form onSubmit={handleFormSubmit} noValidate>
            <FormHeading>
                <Text $variant="title-base" $center>
                    {content.heading}
                </Text>
                <Text $variant="paragraph-large" $color="muted" $center>
                    {content.body}
                </Text>
            </FormHeading>

            <s.InputsWrapper>
                <s.Row>
                    <Select
                        label={globalStrings.country}
                        options={countryList
                            .filter((country) => !supportedLocales.includes(country.code))
                            .map(({code, name}) => ({label: name, value: code.toLowerCase()}))}
                        placeholder={globalStrings.select}
                        value={state.country}
                        onChange={(value) =>
                            handleInputChange('country', value as NotSupportedCountryCode)
                        }
                        error={errors.country}
                        required
                    />
                </s.Row>
            </s.InputsWrapper>

            <s.InputsWrapper>
                <s.Row>
                    <Input
                        label={globalStrings.work_email}
                        value={state.email}
                        onChange={(value) => handleInputChange('email', value)}
                        error={errors.email}
                        required
                    />
                </s.Row>
            </s.InputsWrapper>

            <Consents {...consentsProps} />

            <Button loading={isLoading} type="submit">
                {globalStrings.submit}
            </Button>
        </s.Form>
    )
}

const FormHeading = styled.div`
    display: grid;
    row-gap: ${tokens.spacing12};
`

export const hubspotGlobalFormFragment = graphql`
    fragment HubspotGlobalForm on PrismicHubspotGlobalForm {
        __typename
        data {
            heading
            body
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            imperative_consents_production {
                consent {
                    document {
                        ...ConsentWebsite
                    }
                }
            }
            imperative_consents_staging {
                consent {
                    document {
                        ...ConsentWebsite
                    }
                }
            }
        }
    }
`

export default HubspotGlobalForm
